/**
 * TransferOwnerDialog — reassign ownership of a keybook entry.
 *
 * Used in two flavors driven by `mode`:
 *   - 'transfer' — current owner hands an entry to another email or
 *                  to a team. Voluntary; the audit log shows
 *                  `ownership_transferred`.
 *   - 'claim'    — tenant admin/owner overrides ownership when the
 *                  current owner can't or won't act (offboarded,
 *                  inactive). Audit shows `ownership_claimed`.
 *
 * Both flavors use the same server endpoint (`POST
 * /entries/:id/ownership`) — the server picks the event type based
 * on whether the actor is the current owner.
 */
import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useTheme, getThemeClass, useToast } from '@papan/core-ui/ui'
import { request } from '@papan/api-client'
import { X, AtSign, Users, Loader2, ArrowRight, Tag } from 'lucide-react'
import { keybookApi } from '../client/api.js'
import { erasedOwnerEmail } from '@papan/types'
import { useTranslation } from '@papan/i18n'

interface MembershipOption {
  email: string
  role: string
  name?: string
}

interface TeamOption {
  id: string
  name: string
}

interface TransferOwnerDialogProps {
  entryId: string
  entryLabel: string
  tenantId: string
  /** The entry's current owner — display only. */
  currentOwner: { type: 'email' | 'team' | 'role'; id: string }
  /**
   *  - 'transfer' — current owner hands the entry to another
   *    email or team. Subtitle "pick a new owner."
   *  - 'claim' — admin takes over an orphan entry (owner email no
   *    longer maps to an active member). Pre-selects caller for the
   *    one-click case. Subtitle calls out the orphan reason.
   *  - 'reassign' — admin reassigns an entry whose current owner
   *    IS still active. No orphan copy, no caller pre-select —
   *    the admin picks a new owner deliberately.
   */
  mode: 'transfer' | 'claim' | 'reassign'
  /** Caller's own active org email — pre-selected for claims so a
   *  one-click "Claim" is possible. */
  callerEmail?: string
  /** A personal keybook belongs to people only — no "A team" choice (the server
   *  refuses a team owner there too). Default: an organization. */
  isOrg?: boolean
  onClose: () => void
  onTransferred?: () => void
}

type TargetType = 'email' | 'team'

export function TransferOwnerDialog({
  entryId,
  entryLabel,
  tenantId,
  currentOwner,
  mode,
  callerEmail,
  isOrg = true,
  onClose,
  onTransferred,
}: TransferOwnerDialogProps) {
  const { t } = useTranslation('keybook')
  /** An owner as shown — a deleted account reads "x@y (deleted account)". */
  const ownerLabel = (id: string) => { const gone = erasedOwnerEmail(id); return gone ? t('ownerDeletedAccount', { email: gone }) : id }
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const toast = useToast()

  const [members, setMembers] = useState<MembershipOption[]>([])
  const [teams, setTeams] = useState<TeamOption[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [targetType, setTargetType] = useState<TargetType>('email')
  /** Keep the giver's Manage on a hand-off (default on; the server writes a row
   *  only when they'd otherwise lose it). */
  const [keepManage, setKeepManage] = useState(true)
  const [pickedValue, setPickedValue] = useState<string>(
    mode === 'claim' && callerEmail ? callerEmail.toLowerCase() : '',
  )

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const [memberList, teamList] = await Promise.all([
        request<{ members: MembershipOption[] }>(`/api/orgs/${tenantId}/members`).then(r => r.members ?? []),
        request<{ teams: TeamOption[] }>(`/api/orgs/${tenantId}/teams`).then(r => r.teams),
      ])
      setMembers(memberList)
      setTeams(teamList)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load tenant members + teams')
    } finally {
      setLoading(false)
    }
  }, [tenantId, toast])

  useEffect(() => { void reload() }, [reload])

  // Filter out the current owner from candidates — picking yourself
  // again is a no-op.
  const eligibleEmails = members.filter(m => !(currentOwner.type === 'email' && m.email.toLowerCase() === currentOwner.id))
  const eligibleTeams = teams.filter(t => !(currentOwner.type === 'team' && t.id === currentOwner.id))

  const handleSubmit = async () => {
    if (!pickedValue || busy) return
    setBusy(true)
    try {
      const payload = targetType === 'email'
        ? { ownerType: 'email' as const, ownerId: pickedValue.toLowerCase(), keepManage }
        : { ownerType: 'team' as const, ownerId: pickedValue, keepManage }
      await keybookApi.transferOwner(entryId, payload)
      toast.success(
        mode === 'claim' ? 'Entry claimed'
          : mode === 'reassign' ? 'Ownership reassigned'
            : 'Ownership transferred',
      )
      onTransferred?.()
      onClose()
    } catch (err: any) {
      toast.error(err?.message || 'Transfer failed')
    } finally {
      setBusy(false)
    }
  }

  const title = mode === 'claim' ? 'Claim entry'
    : mode === 'reassign' ? 'Reassign ownership'
      : 'Transfer ownership'
  const subtitle = mode === 'claim'
    ? `${entryLabel} — its current owner is no longer active.`
    : `${entryLabel} — pick a new owner.`

  // Portaled: in-tree fixed overlays underlap the taskbar (ancestor
  // stacking context) — see KeybookView.
  return createPortal(
    <div className="kb-deck-portal fixed inset-0 z-[110] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} ws-glass-dialog w-full max-w-md p-6 relative z-10`}>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 themed-text-muted hover:themed-text"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <h2 className="text-lg font-semibold themed-text mb-1">{title}</h2>
        <p className="text-sm themed-text-muted mb-4">{subtitle}</p>

        {/* Admin reassigning an active entry they don't own. The
            'claim' mode already calls out the orphan reason in its
            subtitle ("its current owner is no longer active"), so we
            only need this callout for 'reassign'. The owner email is
            interpolated so the admin sees whose ownership they're
            moving. */}
        {mode === 'reassign' && currentOwner.type === 'email' && (
          <div
            data-testid="transfer-admin-acting-callout"
            className="mb-4 rounded-md border border-purple-500/30 bg-purple-500/10 p-3 text-xs themed-text leading-relaxed"
          >
            You're reassigning ownership as an admin, not the owner.
            This moves <strong>{ownerLabel(currentOwner.id)}</strong>'s credential
            to someone else and may surprise them.
          </div>
        )}

        <div className="text-xs themed-text-muted mb-4 flex items-center gap-1.5">
          <span>From</span>
          {currentOwner.type === 'email' ? (
            <span className="flex items-center gap-1 themed-text">
              <AtSign size={12} />
              <span className="truncate max-w-[200px]">{ownerLabel(currentOwner.id)}</span>
            </span>
          ) : currentOwner.type === 'role' ? (
            <span className="flex items-center gap-1 themed-text">
              <Tag size={12} />
              <span className="truncate max-w-[200px]">Role: {currentOwner.id}</span>
            </span>
          ) : (
            <span className="flex items-center gap-1 themed-text">
              <Users size={12} />
              <span>Team</span>
            </span>
          )}
          <ArrowRight size={12} className="themed-icon" />
          <span>To…</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-6 themed-text-muted">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {isOrg && <div className="flex gap-2">
              <button
                onClick={() => { setTargetType('email'); setPickedValue(mode === 'claim' && callerEmail ? callerEmail.toLowerCase() : '') }}
                className={`flex-1 px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 ${
                  targetType === 'email' ? 'themed-btn-primary' : 'themed-btn-secondary'
                }`}
              >
                <AtSign size={14} />
                A member
              </button>
              <button
                onClick={() => { setTargetType('team'); setPickedValue('') }}
                className={`flex-1 px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 ${
                  targetType === 'team' ? 'themed-btn-primary' : 'themed-btn-secondary'
                }`}
              >
                <Users size={14} />
                A team
              </button>
            </div>}

            {targetType === 'email' ? (
              <select
                aria-label="New owner email"
                value={pickedValue}
                onChange={e => setPickedValue(e.target.value)}
                className="themed-input w-full px-3 py-2 rounded-lg text-sm"
              >
                <option value="">Pick a member…</option>
                {eligibleEmails.map(m => (
                  <option key={m.email} value={m.email}>{m.email} ({m.role})</option>
                ))}
              </select>
            ) : (
              <select
                aria-label="New owner team"
                value={pickedValue}
                onChange={e => setPickedValue(e.target.value)}
                className="themed-input w-full px-3 py-2 rounded-lg text-sm"
              >
                <option value="">Pick a team…</option>
                {eligibleTeams.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            )}

            {(mode === 'transfer' || mode === 'reassign') && targetType === 'email' && (
              <p className="text-xs themed-text-muted">
                The new owner's personal nickname for this entry (if
                any) becomes the canonical name.{' '}
                {mode === 'transfer'
                  ? null
                  : 'The current owner loses access unless they hold a grant on it.'}
              </p>
            )}
            {mode === 'transfer' && (
              <label className="flex items-center gap-2 text-xs themed-text cursor-pointer" data-testid="transfer-keep-manage">
                <input type="checkbox" checked={keepManage} onChange={e => setKeepManage(e.target.checked)} />
                {t('transfer.keepManage')}
              </label>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={onClose}
                className="themed-btn-secondary px-3 py-2 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!pickedValue || busy}
                data-testid="transfer-confirm"
                className="themed-btn-primary px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {mode === 'claim' ? 'Claim' : mode === 'reassign' ? 'Reassign' : 'Transfer'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  , document.body)
}
